import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import IntlMessages from "@richbeauty/utility/IntlMessages";
import { convertDate, convertDateTime, getSelectUserCommonProps } from "@richbeauty/utility/Utils";
import {
  Button,
  Col,
  Empty,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Row,
  Select,
  Space,
  Steps,
} from "antd";
import { forwardRef, useCallback, useImperativeHandle, useState, useRef, useEffect, useMemo } from "react";
import { DatePicker } from "antd";
import moment from "moment";
import { useIntl } from "react-intl";
import { CUSTOMER_ROLE_NAME, PageDefault, TakeDefault } from "shared/constants/AppConst";
import toast from "react-hot-toast";
import useDeleteTasks from "hooks/apis/task/useDeleteTasks";
import useGetTasks from "hooks/apis/task/useGetTasks";
import useCreateTask from "hooks/apis/task/useCreateTask";
const { TextArea } = Input;

import EditTaskModal from "../../Tasks/CreateAndEditTaskModal/EditTaskModal";
import useGetUsers from "hooks/apis/users/useGetUsers";

const ViewCustomerLeadNotesModel = forwardRef(({ id, userId }, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const { messages } = useIntl();

  const openModal = useCallback(() => {
    setIsVisible(true);
  }, [setIsVisible]);
  const closeModal = useCallback(() => setIsVisible(false), [setIsVisible]);

  useImperativeHandle(
    ref,
    () => ({
      openModal,
      closeModal,
    }),
    [openModal, closeModal]
  );

  const [params, setParams] = useState({
    page: PageDefault,
    take: TakeDefault,
    customerLeadId: id,
    userId
  });

  const { data: nonCustomersRaw } = useGetUsers({
    page: 1,
    take: 1000,
    excludeRole: CUSTOMER_ROLE_NAME
  });

  useEffect(() => {
    setParams({ page: PageDefault, take: TakeDefault, customerLeadId: id, userId });
  }, [id, userId]);

  const selectNonCustomerProps = useMemo(() => getSelectUserCommonProps({ users: nonCustomersRaw?.data }), [nonCustomersRaw?.data]);
  // State cho form tạo note mới
  const [newNoteDueDate, setNewNoteDueDate] = useState(null);
  const [selectedAssignTo, setSelectedAssignTo] = useState(null);
  const { data: tasksData, refetch } = useGetTasks(params);

  // Edit task modal
  const editModalRef = useRef(null);
  const [editTaskId, setEditTaskId] = useState(null);

  const handleOpenEditModal = (taskId) => {
    if (!taskId) return;
    // reset first so reopening same id triggers child update
    setEditTaskId(null);
    setTimeout(() => {
      setEditTaskId(taskId);
      editModalRef.current?.openModal && editModalRef.current.openModal();
    }, 0);
  };

  const handleEditModalClose = useCallback(() => {
    setEditTaskId(null);
    editModalRef.current?.closeModal && editModalRef.current.closeModal();
  }, []);

  const { trigger: triggerCreate, isMutating: isMutatingCreate } =
    useCreateTask();
  const { trigger: triggerDelete } = useDeleteTasks();

  const [newNote, setNewNote] = useState("");

  const onCreateNote = () => {
    triggerCreate(
      {
        customerLeadId: id?.toString(),
        name: newNote,
        dueDate: newNoteDueDate,
        assignToId: selectedAssignTo
      },
      {
        onSuccess: async () => {
          setNewNote("");
          setNewNoteDueDate(null);
          setSelectedAssignTo(null);
          // refetch current list
          refetch && refetch();
        },
        onError: (error) => {
          toast.error(error?.message || messages["app.users.createNote.error"]);
        },
      }
    );
  };

  const onDeleteNote = (noteId) => {
    triggerDelete(
      [noteId],
      {
        onSuccess: async () => {
          refetch && refetch();
        },
        onError: (error) => {
          toast.error(error?.message || messages["app.users.deleteNote.error"]);
        },
      }
    );
  };

  return (
    <Modal
      centered
      open={isVisible}
      onCancel={closeModal}
      footer={null}
      width={1000}
      title={<IntlMessages id="app.customerLeads.notes" />}
    >
      <Row gutter={40}>
        <Col span={12}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Row gutter={[4, 4]}>
              <Col span={24} style={{ marginBottom: 4 }}>
                <TextArea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="New note..."
                />
              </Col>
              <Col span={24} style={{ marginBottom: 8 }}>
                <Row gutter={[4, 4]}>
                  <Col span={16 } >
                    <Select
                      {...selectNonCustomerProps}
                      style={{ width: "100%" }}
                      placeholder="Assign to user"
                      onChange={(value) => { setSelectedAssignTo(value); }}
                      value={selectedAssignTo}
                    />
                  </Col>
                  <Col span={8} >
                    <DatePicker
                      style={{ width: "100%", height: "100%"}}
                      placeholder="Due date"
                      value={newNoteDueDate ? moment(newNoteDueDate, "YYYY-MM-DD") : undefined}
                      onChange={(date, dateString) => {
                        setNewNoteDueDate(date ? dateString : null);
                      }}
                      format="YYYY-MM-DD"
                      allowClear
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <Button
              type="primary"
              disabled={!newNote}
              loading={isMutatingCreate}
              onClick={onCreateNote}
            >
              <IntlMessages id="app.create" />
            </Button>
          </Space>
        </Col>
        <Col
          span={12}
          style={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "50vh",
            overflowY: "auto",
          }}
        >
          {/* <Space > */}
          {tasksData?.data?.length ? (
            <Steps
              progressDot={(_, { index }) => {
                const item = tasksData?.data?.[index];
                const isDue = Boolean(item?.dueDate);
                return (
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 12, height: 12 }}>
                    <span style={{ display: "block", transform: "translateY(-5px) translateX(-2.5px)", width: 8, height: 8, borderRadius: "50%", backgroundColor: isDue ? "#ff4d4f" : "#1890ff" }} />
                  </span>
                );
              }}
              current={tasksData?.data?.length}
              direction="vertical"
              items={tasksData?.data?.map(
                ({ createdUser, name, id: taskId, dueDate, updatedAt, createdAt, status, assignedUser }) => {
                  const createdAtDisplay = convertDateTime(createdAt ?? updatedAt);
                  const dueDateDisplay = dueDate ? convertDate(dueDate) : null;
                  const statusText = status || "";

                  return {
                    title: (
                      <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                        {name}
                      </p>
                    ),
                    description: (
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        <div style={{ margin: 0 }}>{`${createdUser?.username || ""} | ${createdAtDisplay}`}</div>
                        {dueDateDisplay && (
                          <div style={{ color: "rgba(0,0,0,0.45)", marginTop: 4 }}>
                            {`${statusText} | ${dueDateDisplay} ${assignedUser ? `| ${assignedUser?.username}` : ""}`}
                          </div>
                        )}
                      </div>
                    ),
                    subTitle: (
                      <div>
                        <Button
                          type="default"
                          size="small"
                          style={{ margin: 8 }}
                          icon={<EditOutlined />}
                          onClick={() => handleOpenEditModal(taskId)}
                        />
                        <Popconfirm
                          title="Are you sure to delete this task?"
                          onConfirm={() => onDeleteNote(taskId)}
                          okText="Yes"
                          cancelText="No"
                          zIndex={9999}
                        >
                          <Button
                            danger
                            size="small"
                            style={{ margin: 8 }}
                            icon={<DeleteOutlined />}
                          />
                        </Popconfirm>
                      </div>
                    ),
                  };
                }
              )}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
          {/* </Space> */}
          {tasksData?.data?.meta?.totalCount > TakeDefault && (
            <Pagination
              current={params.page}
              style={{ alignSelf: "flex-end" }}
              onChange={(page, pageSize) => {
                setParams((prev) => {
                  return {
                    ...prev,
                    page: page,
                    take: pageSize,
                  };
                });
              }}
              total={tasksData?.data?.meta?.totalCount}
              showTotal={(total, range) => (
                <span>
                  {range[0]}-{range[1]} <IntlMessages id="common.on" /> {total} records
                </span>
              )}
              showSizeChanger
            />
          )}
        </Col>
      </Row>
      <EditTaskModal ref={editModalRef} id={editTaskId} onClose={handleEditModalClose} />
    </Modal>
  );
});

export default ViewCustomerLeadNotesModel;
